import { List, Map, Record } from "immutable";
import { ProductRecord } from "common/records/Product";
import { RootState, RootActions } from "common/reducers";
import { productFromJSON } from "common/records/Product";
import { reducer as idsManagementReducer } from "common/reducers/idsManagement.reducer";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { receive, fetch, reset, loaded } from "common/reducers/Utils";
import { reducer as imageReducer } from "common/reducers/image.reducer";
import { reducer as documentReducer } from "common/reducers/document.reducer";
import { reducer as skuReducer } from "common/reducers/sku.reducer";
import * as documentActions from "common/reducers/document.actions";
import { ImageActions } from "common/reducers/image.actions";
import {
  ADD_DOCUMENT_TO_PRODUCT,
  DESTROY_PRODUCT_DOCUMENT,
  DESTROY_PRODUCT_IMAGE,
  FETCH_PRODUCT,
  PRODUCT_DOCUMENT_DELETE,
  PRODUCT_DOCUMENT_SORT,
  ProductActions,
  RECEIVE_PRODUCT,
  RECEIVE_PRODUCT_DOCUMENTS,
  RECEIVE_PRODUCT_IMAGES,
  REMOVE_PRODUCT,
  RESET_PRODUCT,
  SET_PRODUCT_LOADED,
  RECEIVE_PRODUCT_OVERVIEW,
  RECEIVE_PRODUCT_OVERVIEWS,
  SET_PRODUCT_LEAF,
} from "common/reducers/product.actions";
import { SkuActions } from "common/reducers/sku.actions";
import { ProductToOptionGroupLinkActions } from "common/reducers/productToOptionGroupLink.actions";
import { reducer as productToOptionGroupLinkReducer } from "common/reducers/productToOptionGroupLink.reducer";

export const ProductStateRecord = Record({
  productOverviewsById: Map<number, ProductRecord>(),
  byId: Map<number, ProductRecord>(),
  lastSavedById: Map<number, ProductRecord>(),
});

export const initialState = ProductStateRecord();

export type ProductState = typeof initialState;

export type ProductReducerState = RootState;

export const reducer = (state: RootState, action: RootActions): RootState => {
  const model = "products";
  if (state && !state.getIn([model])) {
    state = state.setIn([model], initialState);
  }

  switch (action.type) {
    case FETCH_PRODUCT:
      return fetch(state, model, productFromJSON({ id: action.payload.productId })) as RootState;

    case RECEIVE_PRODUCT_OVERVIEW:
      const productOverview = action.payload.productOverview;
      state = state.setIn([model, "productOverviewsById", productOverview.id], productFromJSON(productOverview));
      if (productOverview.images?.length) {
        productOverview.images.forEach((image) => {
          state = imageReducer(state, ImageActions.receiveImageOverview(image));
        });
      }
      if (productOverview.documents?.length) {
        productOverview.documents.forEach((document) => {
          state = documentReducer(state, documentActions.DocumentActions.receiveDocument(document));
        });
      }
      if (productOverview.children?.length) {
        productOverview.children.forEach((child) => {
          state = reducer(state, ProductActions.receiveProductOverview(child));
        });
      }

      return state;

    case RECEIVE_PRODUCT:
      const product: ProductRecord = productFromJSON(action.payload.product);
      if (action.payload.options.pushToList && action.payload.options.model) {
        state = idsManagementReducer(state, IdsManagementActions.pushId(product.id, action.payload.options.model));
      }
      state = receive(state, model, product) as RootState;

      if (action.payload.product.documents) {
        action.payload.product.documents.map((doc) => {
          state = documentReducer(state, documentActions.DocumentActions.receiveDocument(doc));
        });
      }

      if (action.payload.product.images) {
        action.payload.product.images.map((img) => {
          state = imageReducer(state, ImageActions.receiveImage(img));
        });
      }

      if (action.payload.product.sku) {
        state = skuReducer(state, SkuActions.receiveSku(action.payload.product.sku));
      }

      if (action.payload.product.ptog_links) {
        action.payload.product.ptog_links.map((ptog) => {
          state = productToOptionGroupLinkReducer(
            state,
            ProductToOptionGroupLinkActions.receiveProductToOptionGroupLink(ptog),
          );
        });
      }

      return state;

    case RECEIVE_PRODUCT_OVERVIEWS:
      const productOverviews = List(action.payload.products);
      productOverviews.forEach((productOverview) => {
        state = reducer(state, ProductActions.receiveProductOverview(productOverview));
      });
      return state;

    case RESET_PRODUCT:
      return reset(state, model, action.payload.productId) as RootState;

    case REMOVE_PRODUCT: {
      state = idsManagementReducer(state, IdsManagementActions.removeId(action.payload.productId, model));
      return state;
    }
    case SET_PRODUCT_LOADED:
      return loaded(state, model, action.payload.productId) as RootState;

    case RECEIVE_PRODUCT_DOCUMENTS:
      state = state.setIn([model, "byId", action.payload.productId, "document_ids"], action.payload.documentIds);
      return state;

    case DESTROY_PRODUCT_DOCUMENT:
      let documentRecord = state.getIn(["documents", "byId"]).get(action.payload.documentId);
      documentRecord = documentRecord?.set("_destroy", true);
      state = state.setIn(["documents", "byId", action.payload.documentId], documentRecord);

      return state;

    case RECEIVE_PRODUCT_IMAGES:
      state = state.setIn([model, "byId", action.payload.productId, "image_ids"], action.payload.imageIds);
      return state;

    case DESTROY_PRODUCT_IMAGE:
      let imageRecord = state.getIn(["images", "byId"]).get(action.payload.imageId);
      imageRecord = imageRecord?.set("_destroy", true);
      state = state.setIn(["images", "byId", action.payload.imageId], imageRecord);

      return state;

    case ADD_DOCUMENT_TO_PRODUCT:
      const { productId, files, kind } = action.payload;
      files.forEach((file) => {
        const newDocument = {
          file_queue: file,
          sort_order: 0,
          display: "do_not_display" as any,
          name: file.name,
        };
        if (kind === "document") {
          state = documentReducer(state, documentActions.DocumentActions.productDocument(productId, newDocument));
        } else if (kind === "image") {
          state = imageReducer(state, ImageActions.productImage(productId, newDocument));
        }
      });
      return state;

    case PRODUCT_DOCUMENT_SORT:
      const { oldIndex, newIndex } = action.payload;

      const productRecord = state.getIn([model, "byId", action.payload.productId]) as ProductRecord;
      if (productRecord) {
        let ids = List<number>();
        if (action.payload.kind === "document") {
          ids = productRecord.get("document_ids");
        } else if (action.payload.kind === "image") {
          ids = productRecord.get("image_ids");
        }
        const itemId = ids.get(oldIndex);
        if (itemId) {
          ids = ids.delete(oldIndex).insert(newIndex, itemId);
          state = state.setIn(
            [
              model,
              "byId",
              action.payload.productId,
              action.payload.kind === "document" ? "document_ids" : "image_ids",
            ],
            ids,
          );
        }
      }
      return state;

    case PRODUCT_DOCUMENT_DELETE:
      const record = state.getIn([model, "byId", action.payload.productId]) as ProductRecord;
      if (record) {
        let ids = List<number>();
        if (action.payload.kind === "document") {
          ids = record.get("document_ids");
        } else if (action.payload.kind === "image") {
          ids = record.get("image_ids");
        }
        if (action.payload.documentId < 0) {
          ids = ids.filter((x: number) => x !== action.payload.documentId);
          state = state.setIn(
            [
              model,
              "byId",
              action.payload.productId,
              action.payload.kind === "document" ? "document_ids" : "image_ids",
            ],
            ids,
          );
        } else {
          if (action.payload.kind === "document") {
            state = reducer(state, ProductActions.destroyProductDocument(action.payload.documentId));
          } else if (action.payload.kind === "image") {
            state = reducer(state, ProductActions.destroyProductImage(action.payload.documentId));
          }
        }
      }
      return state;

    case SET_PRODUCT_LEAF: {
      const { productId, leaf } = action.payload;
      return state.setIn([model, "productOverviewsById", productId, "leaf"], leaf);
    }

    default:
      return state;
  }
};
